// WS5 PR5 — Ride motion math (pure).
//
// 3-phase eased velocity (accel 0-20% / cruise 20-80% / decel 80-100%),
// linear path between startPos and targetPos, look-guided yaw/pitch blend
// con wrap-around protection per yaw (range [-π, π], rotazione shorter-arc).
//
// Pure functions, no side-effect, no allocations modulo-scoped (caller
// passa scratch buffers se serve aggressive zero-alloc — qui ritorniamo
// tuple Vec3 per chiarezza, costo trascurabile a 60fps per 1 ride/volta).

import {
  RIDE_ACCEL_FRACTION,
  RIDE_CRUISE_SPEED_MPS,
  RIDE_DECEL_FRACTION,
  RIDE_MAX_DURATION_MS,
  RIDE_MIN_DURATION_MS,
  type Vec3,
} from './types.js';

/** Clamp helper. */
export function clamp(x: number, lo: number, hi: number): number {
  if (x < lo) return lo;
  if (x > hi) return hi;
  return x;
}

/** Lerp helper. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Distanza euclidea 3D. Pure. */
export function distance3(a: Vec3, b: Vec3): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** Calcola durata ride da distanza Euclidea. Formula:
 *  durationMs = distance_m / cruise_speed_mps * 1000, clampata in
 *  [RIDE_MIN_DURATION_MS, RIDE_MAX_DURATION_MS].
 *
 *  Esempi:
 *  - distance=1m → 167ms → clamp a 350ms (snap rapido evita scatti).
 *  - distance=3m → 500ms.
 *  - distance=5.4m → 900ms (boundary).
 *  - distance=10m → 1667ms → clamp a 900ms (UX skip rapido scena ampia).
 *
 *  Caller deve garantire distance >= 0 + finite. */
export function computeRideDuration(distanceM: number): number {
  if (!Number.isFinite(distanceM) || distanceM < 0) return RIDE_MIN_DURATION_MS;
  const raw = (distanceM / RIDE_CRUISE_SPEED_MPS) * 1000;
  return clamp(raw, RIDE_MIN_DURATION_MS, RIDE_MAX_DURATION_MS);
}

/** Eased t01 → eased01 con 3-phase curve.
 *  - [0, 0.2]: accel quadratica (t^2 / 0.2 * 0.1) — copre il 10% del tragitto.
 *  - [0.2, 0.8]: cruise lineare (90% del tragitto in 60% del tempo).
 *  - [0.8, 1]: decel quadratica simmetrica (copre il 10% del tragitto).
 *
 *  Garantisce: f(0)=0, f(1)=1, f'(0)=0 (no jerk start), f'(1)=0 (no jerk end).
 *  Continuita' C1 alle giunzioni 0.2 e 0.8.
 *
 *  Razionale UX: "metro mobile" — accel/decel smooth ma cruise lineare al
 *  90% del tragitto in 60% del tempo (= velocita' reale di crociera supera
 *  RIDE_CRUISE_SPEED_MPS del 50% per breve tratto, accettabile).
 *
 *  Pure, testabile, monotona crescente. Implementata senza branch-heavy. */
export function easedT(t01: number): number {
  if (!Number.isFinite(t01)) return 0;
  const t = clamp(t01, 0, 1);
  // Fast-path boundary (evita floating-point drift su t=1 con curve asimmetriche).
  if (t === 0) return 0;
  if (t === 1) return 1;
  const accelEnd = RIDE_ACCEL_FRACTION;
  const decelStart = 1 - RIDE_DECEL_FRACTION;
  // Area sotto velocity curve = 1 (=normalized distance). Per accel/decel
  // linear ramps + cruise piatto: area = 0.5*accelEnd*v + (decelStart-accelEnd)*v + 0.5*(1-decelStart)*v = 1.
  // Risolvo per v: v * (0.5*accelEnd + decelStart - accelEnd + 0.5*(1-decelStart)) = 1.
  // Esempi: accel=0.2, decel=0.2 → v=1.25. accel=0, decel=0.1 → v=1.0526.
  const denominator =
    0.5 * accelEnd + (decelStart - accelEnd) + 0.5 * RIDE_DECEL_FRACTION;
  if (denominator <= 0) return t; // safety: degenerate accel=decel=0.5 → linear identity
  const peakVelocity = 1 / denominator;

  // Accel branch — solo se accelEnd > 0 (guard divisione per 0 con
  // RIDE_ACCEL_FRACTION=0, Unity-snappy mode).
  if (accelEnd > 0 && t <= accelEnd) {
    return (peakVelocity * t * t) / (2 * accelEnd);
  }
  if (t < decelStart) {
    const posAtAccelEnd = (peakVelocity * accelEnd) / 2;
    return posAtAccelEnd + peakVelocity * (t - accelEnd);
  }
  const decelFraction = 1 - decelStart;
  const posAtAccelEnd = (peakVelocity * accelEnd) / 2;
  const posAtDecelStart = posAtAccelEnd + peakVelocity * (decelStart - accelEnd);
  // Decel branch — guard simmetrico per decelFraction=0.
  if (decelFraction <= 0) return 1;
  const dt = t - decelStart;
  return posAtDecelStart + peakVelocity * (dt - (dt * dt) / (2 * decelFraction));
}

/** Linear lerp di Vec3 (path lineare, NON curva). */
export function lerpVec3(a: Vec3, b: Vec3, t: number): Vec3 {
  const ct = clamp(t, 0, 1);
  return [lerp(a[0], b[0], ct), lerp(a[1], b[1], ct), lerp(a[2], b[2], ct)];
}

/** Normalize angle a [-π, π]. */
export function wrapAnglePi(angle: number): number {
  if (!Number.isFinite(angle)) return 0;
  // ((angle + π) mod 2π) - π. Mod TS: (a%b + b) % b per gestire negativi.
  const twoPi = Math.PI * 2;
  let a = (angle + Math.PI) % twoPi;
  if (a < 0) a += twoPi;
  return a - Math.PI;
}

/** Lerp angolare shorter-arc. Gestisce wrap-around 2π:
 *  - lerpAngle(3.0, -3.0, 0.5) → ~3.14 (non 0 = path "lungo").
 *
 *  Pure. Tested edge cases: 0→π/2, π→-π, 3→-3, 0→2π. */
export function lerpAngle(a: number, b: number, t: number): number {
  if (!Number.isFinite(a)) return Number.isFinite(b) ? b : 0;
  if (!Number.isFinite(b)) return a;
  const aw = wrapAnglePi(a);
  const bw = wrapAnglePi(b);
  let diff = bw - aw;
  if (diff > Math.PI) diff -= 2 * Math.PI;
  else if (diff < -Math.PI) diff += 2 * Math.PI;
  return aw + diff * clamp(t, 0, 1);
}

/** Calcola yaw + pitch che fanno guardare la camera da `from` verso `to`.
 *  Convenzione: yaw=0 → forward=(0,0,-1), positive yaw → rotation around +Y
 *  (CCW vista dall'alto). pitch positive → look up.
 *
 *  Formula derivata dal forwardVector usato in walk:
 *    fwdX = -sin(yaw) * cos(pitch)
 *    fwdY = sin(pitch)
 *    fwdZ = -cos(yaw) * cos(pitch)
 *  Quindi:
 *    pitch = asin(fwdY / |fwd|) = asin((to.y - from.y) / distance3)
 *    yaw   = atan2(-fwdX, -fwdZ) = atan2(-(to.x - from.x), -(to.z - from.z))
 *
 *  Caller deve garantire from != to (distance > 0). Return [0, 0] se degenere. */
export function lookAtYawPitch(from: Vec3, to: Vec3): { yaw: number; pitch: number } {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const dz = to[2] - from[2];
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (dist < 1e-6) return { yaw: 0, pitch: 0 };
  const pitch = Math.asin(clamp(dy / dist, -1, 1));
  const yaw = Math.atan2(-dx, -dz);
  return { yaw, pitch };
}

/** Calcola pos + yaw + pitch interpolati al tempo nowMs dall'INIZIO del ride.
 *  Pure. Caller passa parametri ride (start/target/anchor/duration/startTime)
 *  + nowMs.
 *
 *  Ritorna:
 *  - pos: lerp lineare startPos→targetPos via easedT(t01).
 *  - yaw/pitch: lerpAngle/lerp da startYaw/startPitch a targetYaw/targetPitch
 *    via easedT(t01) — look-guided smooth. */
export function computeRideTransform(params: {
  startPos: Vec3;
  startYaw: number;
  startPitch: number;
  targetPos: Vec3;
  targetYaw: number;
  targetPitch: number;
  startTimeMs: number;
  durationMs: number;
  nowMs: number;
}): { pos: Vec3; yaw: number; pitch: number; t01: number } {
  const { startPos, startYaw, startPitch, targetPos, targetYaw, targetPitch } = params;
  const elapsed = Math.max(0, params.nowMs - params.startTimeMs);
  const t01 = params.durationMs > 0 ? clamp(elapsed / params.durationMs, 0, 1) : 1;
  const e = easedT(t01);
  const pos = lerpVec3(startPos, targetPos, e);
  const yaw = lerpAngle(startYaw, targetYaw, e);
  const pitch = lerp(startPitch, targetPitch, e);
  return { pos, yaw, pitch, t01 };
}
